import { ModalDialog } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { HostingBackupCatalogCard } from './HostingBackupCatalogCard';
import { declaredHostingPanels } from './hosting-technology-catalogue';
import { useEffect, useId, useRef, useState } from 'react';
import type {
  HostingDeclaredSiteView,
  HostingPleskSiteProfileInput,
  HostingServerView,
} from '@tosho/contracts';
import type { HostingClientChoice } from './HostingClientPicker';
import { EMPTY_SITE_PROFILE, SITE_CMS, SITE_ENVIRONMENTS } from './hosting-site-profile';
import { hostingSitesApi, HostingSiteRequestError, hostingSiteError } from './hosting-sites-api';
import { PrototypeIcon } from './PrototypeIcon';
export function HostingDeclaredSiteEditor({
  site,
  serverId,
  servers,
  clients,
  csrfToken,
  onClose,
  onSaved,
  moreServers,
  loadServers,
  moreClients,
  loadClients,
  serverError,
  clientsError,
  retryServers,
  retryClients,
}: {
  readonly site: HostingDeclaredSiteView | null;
  readonly serverId: string;
  readonly servers: readonly HostingServerView[];
  readonly clients: readonly HostingClientChoice[];
  readonly csrfToken: string;
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly moreServers: boolean;
  readonly loadServers: () => void;
  readonly moreClients: boolean;
  readonly loadClients: () => void;
  readonly serverError: boolean;
  readonly clientsError: boolean;
  readonly retryServers: () => void;
  readonly retryClients: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null),
    title = useId();
  const [host, setHost] = useState(site?.serverId ?? serverId),
    [url, setUrl] = useState(site?.url ?? ''),
    [profile, setProfile] = useState<HostingPleskSiteProfileInput>(
      site?.profile ?? EMPTY_SITE_PROFILE,
    ),
    [target, setTarget] = useState(
      site?.organizationSite ? 'ORGANIZATION' : (site?.client?.id ?? 'UNASSIGNED'),
    ),
    [busy, setBusy] = useState(false),
    [locked, setLocked] = useState(false),
    [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const previous = document.activeElement;
    const element = dialog.current;
    element?.showModal();
    return () => {
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);
  function patch<K extends keyof HostingPleskSiteProfileInput>(
    key: K,
    value: HostingPleskSiteProfileInput[K],
  ): void {
    setProfile((p) => ({ ...p, [key]: value }));
  }
  async function self(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const value = await hostingSitesApi.self();
      setUrl(value.url);
      setProfile({ ...EMPTY_SITE_PROFILE, cms: 'FROM_SCRATCH', environment: value.environment });
      setTarget('ORGANIZATION');
    } catch {
      setError(
        'La configuration de cette instance n’est pas disponible. Vous pouvez renseigner son adresse.',
      );
    } finally {
      setBusy(false);
    }
  }
  async function save(): Promise<void> {
    if (busy || locked) return;
    setBusy(true);
    setError(null);
    try {
      await hostingSitesApi.save(
        site,
        {
          ...profile,
          otherCms: profile.cms === 'OTHER' ? profile.otherCms : null,
          serverId: host,
          url,
          organizationSite: target === 'ORGANIZATION',
          clientId: target === 'UNASSIGNED' || target === 'ORGANIZATION' ? null : target,
        },
        csrfToken,
      );
      onSaved();
    } catch (failure) {
      setError(hostingSiteError(failure));
      if (failure instanceof HostingSiteRequestError && failure.code === 'STALE_RECORD')
        setLocked(true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <ModalDialog
      ref={dialog}
      className="hosting-servers-editor hosting-site-editor hosting-declared-editor"
      aria-labelledby={title}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      close={
        <ModalCloseButton
          className="tosho-modal-close"
          aria-label="Fermer"
          disabled={busy}
          onClick={onClose}
        />
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <header className="hosting-site-editor__heading tosho-modal-header">
          <div>
            <p className="hosting-servers-editor__eyebrow">Sites et environnements</p>
            <h2 id={title}>{site ? 'Modifier le site déclaré' : 'Référencer un site'}</h2>
          </div>
        </header>
        <div className="hosting-declared-editor__source">
          <PrototypeIcon name="network" />
          <div>
            <strong>Hébergement hors Plesk</strong>
            <p>
              Référencez un site et son environnement. La santé, le stockage et les sauvegardes
              restent distincts de cette déclaration.
            </p>
          </div>
        </div>
        {!site ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void self()}
            className="hosting-declared-editor__self"
          >
            <PrototypeIcon name="sparkles" />
            Préremplir avec cette instance Tosho
          </button>
        ) : null}
        {error ? (
          <p role="alert" className="hosting-servers__error tosho-feedback is-error">
            {error}
          </p>
        ) : null}
        <fieldset disabled={busy || locked}>
          {serverError ? (
            <p className="tosho-feedback is-error" role="alert">
              La liste des serveurs n’a pas pu être chargée.{' '}
              <button type="button" onClick={retryServers}>
                Réessayer les serveurs
              </button>
            </p>
          ) : null}
          <label>
            Serveur
            <select required value={host} onChange={(e) => setHost(e.target.value)}>
              <option value="">Choisir un serveur</option>
              {site && !servers.some((s) => s.id === site.serverId) ? (
                <option value={site.serverId}>{site.serverLabel}</option>
              ) : null}
              {servers
                .filter(
                  (s) =>
                    s.administration === 'ORGANIZATION' &&
                    (s.id === site?.serverId ||
                      !declaredHostingPanels(s).some(
                        (p) => p.kind === 'PLESK' && p.present === true,
                      )),
                )
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
            </select>
          </label>
          {moreServers ? (
            <button type="button" onClick={loadServers}>
              Charger d’autres serveurs
            </button>
          ) : null}
          <label>
            Adresse du site
            <input
              type="url"
              required
              maxLength={2048}
              placeholder="https://dev.exemple.fr"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </label>
          <small>Adresse publique HTTP ou HTTPS, sans identifiant, paramètre ni chemin.</small>
          <label>
            Rattachement
            <select value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="UNASSIGNED">À rattacher</option>
              <option value="ORGANIZATION">Site de votre organisation</option>
              {site?.client && !clients.some((c) => c.id === site.client?.id) ? (
                <option value={site.client.id}>{site.client.displayName}</option>
              ) : null}
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </label>
          {clientsError ? (
            <p className="tosho-feedback is-error" role="alert">
              La liste des Clients n’a pas pu être chargée.{' '}
              <button type="button" onClick={retryClients}>
                Réessayer les Clients
              </button>
            </p>
          ) : null}
          {moreClients ? (
            <button type="button" onClick={loadClients}>
              Charger d’autres Clients
            </button>
          ) : null}
          <div className="hosting-servers-editor__technical">
            <label>
              CMS
              <select
                value={profile.cms}
                onChange={(e) =>
                  setProfile((p) => ({
                    ...p,
                    cms: e.target.value as HostingPleskSiteProfileInput['cms'],
                    otherCms: null,
                  }))
                }
              >
                {Object.entries(SITE_CMS).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Environnement
              <select
                value={profile.environment}
                onChange={(e) =>
                  patch(
                    'environment',
                    e.target.value as HostingPleskSiteProfileInput['environment'],
                  )
                }
              >
                {Object.entries(SITE_ENVIRONMENTS).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {profile.cms === 'OTHER' ? (
            <label>
              Nom du CMS
              <input
                required
                maxLength={64}
                value={profile.otherCms ?? ''}
                onChange={(e) => patch('otherCms', e.target.value)}
              />
            </label>
          ) : null}
          <label>
            Date de création du site <span>(facultatif, date déclarée)</span>
            <input
              type="date"
              min="1000-01-01"
              max="9999-12-31"
              value={profile.createdOn ?? ''}
              onChange={(e) => patch('createdOn', e.target.value || null)}
            />
          </label>
        </fieldset>
        {site && <HostingBackupCatalogCard serverId={site.serverId} siteId={site.id} />}
        <footer>
          <button type="button" disabled={busy} onClick={onClose}>
            {locked ? 'Fermer et actualiser' : 'Annuler'}
          </button>
          <button
            type="submit"
            className="hosting-servers__primary"
            disabled={busy || locked || !host || !url}
          >
            {busy ? 'Enregistrement…' : site ? 'Enregistrer' : 'Référencer le site'}
          </button>
        </footer>
      </form>
    </ModalDialog>
  );
}
